import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, defer, finalize } from 'rxjs';
import { CommonService } from './common.service';

export interface LoaderState {
  isLoading: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  private activeCount = 0;
  private readonly stateSubject = new BehaviorSubject<LoaderState>({
    isLoading: false,
    message: '',
  });

  public readonly state$ = this.stateSubject.asObservable();
  public readonly isLoading$ = new BehaviorSubject<boolean>(false);
  public readonly message$ = new BehaviorSubject<string>('');

  constructor(
    private shared: CommonService,
    private ngZone: NgZone
  ) {}

  /**
   * Returns current active request count
   */
  get count(): number {
    return this.activeCount;
  }

  /**
   * Increments active loader count and displays the loader with an optional message.
   */
  show(message: string = 'Loading...'): void {
    this.activeCount++;
    this.updateState(true, message);
  }

  /**
   * Decrements active loader count. When count reaches 0, hides the loader.
   */
  hide(): void {
    this.activeCount = Math.max(0, this.activeCount - 1);
    if (this.activeCount === 0) {
      this.updateState(false, '');
    }
  }

  /**
   * Immediately clears all active requests and hides the loader.
   */
  forceHide(): void {
    this.activeCount = 0;
    this.updateState(false, '');
  }

  /**
   * Wraps an Observable with automatic show() on subscription
   * and hide() on finalize (handles success, error, and cancellation).
   */
  wrap<T>(source$: Observable<T>, message: string = 'Loading...'): Observable<T> {
    return defer(() => {
      this.show(message);
      return source$.pipe(
        finalize(() => this.hide())
      );
    });
  }

  private updateState(isLoading: boolean, message: string): void {
    const apply = () => {
      this.shared.isLoading = isLoading; // sync with existing shared.isLoading
      this.isLoading$.next(isLoading);
      this.message$.next(message);
      this.stateSubject.next({ isLoading, message });
    };

    if (NgZone.isInAngularZone()) {
      apply();
    } else {
      this.ngZone.run(apply);
    }
  }
}

